/**
 * Sync bag coparenting serveur ↔ local — Phase 4, incrément 4.10.4.
 *
 * Quatrième sub-entité **coparenting** synchronisée. Étend ADR 0009
 * (server-wins) au champ `data.bag` des foyers coparenting.
 *
 * Symétrique de liaison-sync (4.10.2) : le `status` et la `location`
 * sont mutables côté serveur. Le sub-sync 4.10.4 migre les items
 * pré-auth via POST et rapatrie l'autorité via GET. Les mutations
 * `toggleBagItemStatus` / `transferBagItem` / `setBagItemLocation`
 * appellent `patchBagItem` côté UI au moment de l'action utilisateur
 * (câblage UI hors scope 4.10.4).
 *
 * Idempotent, filtre `home.mode === 'coparenting'`, déclenché en
 * parallèle des autres sub-syncs par `homes-sync.ts`.
 */
import * as bagApi from '../api/bag.js';
import { ApiError } from '../api/client.js';
import {
  applyBagSyncResult,
  getHomesSnapshot,
  isServerHomeId,
  type ServerBagItemPayload,
} from './homes.js';
import type { BagItem, CoparentingHomeData } from '@nestor/shared-types';

export interface BagSyncStatus {
  state: 'idle' | 'syncing' | 'ok' | 'error';
  lastAt: number | null;
  errorCode: string | null;
}

let status: BagSyncStatus = { state: 'idle', lastAt: null, errorCode: null };
let inFlight: Promise<void> | null = null;

export function getBagSyncStatus(): BagSyncStatus {
  return status;
}

/**
 * Synchronise les bag items pour tous les foyers UUID coparenting.
 * Idempotent.
 */
export async function syncBagForAllHomes(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = doSync().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doSync(): Promise<void> {
  status = { state: 'syncing', lastAt: status.lastAt, errorCode: null };
  const { homes } = getHomesSnapshot();

  let anyError = false;
  let lastErrorCode: string | null = null;

  for (const home of homes) {
    if (!isServerHomeId(home.id)) continue;
    if (home.mode !== 'coparenting') continue;

    const data = home.data as CoparentingHomeData;

    // 1. Migration POST des items locaux pré-auth.
    // Phase 3.3.3 : offline-first via SendResult (cf. 3.3.2).
    const idMap = new Map<string, string>();
    const localOnly = data.bag.filter((b) => !isServerHomeId(b.id));
    for (const item of localOnly) {
      try {
        const result = await bagApi.createBagItem(home.id, buildCreateInput(item), item.id);
        if (result.kind === 'sent') {
          idMap.set(item.id, result.data.item.id);
        }
      } catch (err) {
        const code = errorCode(err);
        anyError = true;
        lastErrorCode = code;
        console.warn('[bag-sync] migration POST échouée', {
          homeId: home.id,
          itemId: item.id,
          error: code,
        });
      }
    }

    // 2. Fetch + 3. apply.
    try {
      const serverItems = await bagApi.listBagItems(home.id);
      const payload: ServerBagItemPayload[] = serverItems.map((b) => ({
        id: b.id,
        item: b.item,
        category: b.category,
        location: b.location,
        status: b.status,
        icon: b.icon,
        requiredFor: b.requiredFor,
      }));
      applyBagSyncResult(home.id, { idMap, serverItems: payload });
    } catch (err) {
      if (idMap.size > 0) {
        applyBagSyncResult(home.id, { idMap });
      }
      const code = errorCode(err);
      anyError = true;
      lastErrorCode = code;
      console.warn('[bag-sync] fetch /bag échoué', { homeId: home.id, error: code });
    }
  }

  status = anyError
    ? { state: 'error', lastAt: Date.now(), errorCode: lastErrorCode }
    : { state: 'ok', lastAt: Date.now(), errorCode: null };
}

function buildCreateInput(item: BagItem): bagApi.CreateBagItemInput {
  return {
    item: item.item,
    category: item.category,
    location: item.location,
    status: item.status,
    ...(item.icon ? { icon: item.icon } : {}),
    ...(item.requiredFor ? { requiredFor: item.requiredFor } : {}),
  };
}

function errorCode(err: unknown): string {
  if (err instanceof ApiError) {
    return err.code ?? `http_${err.status.toString()}`;
  }
  if (err instanceof Error) return err.name;
  return 'unknown';
}

/** @internal pour tests — reset le state module-level. */
export function _resetBagSyncForTests(): void {
  status = { state: 'idle', lastAt: null, errorCode: null };
  inFlight = null;
}
